import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VENDOR_CATEGORIES,
  VENDOR_STATUSES,
  balanceCents,
  paidCents,
  type Vendor,
  type VendorCategory,
  type VendorStatus,
} from '@guabi/shared';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { Pill } from '@/components/ui/Pill';
import { money } from '@/lib/format';
import { ApiRequestError } from '@/lib/api';
import { Documents } from '@/features/documents/Documents';
import { PaymentSchedule } from './PaymentSchedule';
import {
  useCreateVendor,
  useDeleteVendor,
  useUpdateVendor,
  useVendorEmailDraft,
  type VendorEmailKind,
} from './queries';

/**
 * One supplier: who they are, what they quoted, and the schedule of what is
 * owed them.
 *
 * The schedule is only editable on a saved vendor. It has its own endpoints — a
 * PATCH of the vendor deliberately cannot touch it — so there is nothing
 * sensible to do with instalments for a vendor that does not exist yet.
 */
export function VendorDrawer({
  wedId,
  vendor,
  currency,
  open,
  onClose,
  canWrite = true,
  onCreated,
}: {
  wedId: string;
  /** Absent when adding. */
  vendor?: Vendor;
  currency: string;
  open: boolean;
  onClose: () => void;
  /** A read-only member may open this to read it, and change nothing. */
  canWrite?: boolean;
  onCreated?: (vendor: Vendor) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateVendor(wedId);
  const update = useUpdateVendor(wedId);
  const remove = useDeleteVendor(wedId);

  const [name, setName] = useState(vendor?.name ?? '');
  const [category, setCategory] = useState<VendorCategory>(vendor?.category ?? 'other');
  const [status, setStatus] = useState<VendorStatus>(vendor?.status ?? 'researching');
  const [quote, setQuote] = useState(vendor?.quoteCents ? String(vendor.quoteCents / 100) : '');
  const [deposit, setDeposit] = useState(
    vendor?.depositCents ? String(vendor.depositCents / 100) : '',
  );
  const [contactName, setContactName] = useState(vendor?.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(vendor?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(vendor?.contactPhone ?? '');
  const [website, setWebsite] = useState(vendor?.website ?? '');
  const [notes, setNotes] = useState(vendor?.notes ?? '');
  const [error, setError] = useState<string>();
  const [confirming, setConfirming] = useState(false);

  const busy = create.isPending || update.isPending || remove.isPending;
  const balance = vendor ? balanceCents(vendor) : undefined;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);

    const fields = {
      name,
      category,
      status,
      // Money is integer cents everywhere; the input collects whole currency.
      ...(quote ? { quoteCents: Math.round(Number(quote) * 100) } : {}),
      ...(deposit ? { depositCents: Math.round(Number(deposit) * 100) } : {}),
      contactName,
      ...(contactEmail ? { contactEmail } : {}),
      contactPhone,
      ...(website ? { website } : {}),
      notes,
      currency,
    };

    try {
      if (vendor) {
        await update.mutateAsync({
          vendorId: vendor.vendorId,
          input: { ...fields, updatedAt: vendor.updatedAt },
        });
        onClose();
      } else {
        const created = await create.mutateAsync(fields);
        onClose();
        // Straight into the saved vendor, which is where the schedule lives.
        onCreated?.(created);
      }
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'version_conflict'
          ? t('vendors.conflict')
          : t('common.somethingWrong'),
      );
    }
  }

  async function onDelete() {
    if (!vendor) return;
    setConfirming(false);
    try {
      await remove.mutateAsync({ vendorId: vendor.vendorId });
      onClose();
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  return (
    <Drawer
      open={open}
      title={vendor ? vendor.name : t('vendors.addTitle')}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          {canWrite && (
            <Button type="submit" form="vendor-form" loading={busy}>
              {t('common.save')}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* What is settled and what is not, before any of the fields. */}
        {vendor && (
          <p className="flex flex-wrap items-center gap-2 text-caption">
            <span className="tabular text-leaf-soft">
              {t('vendors.paidOf', {
                paid: money(paidCents(vendor), vendor.currency ?? currency),
                quote:
                  vendor.quoteCents === undefined
                    ? '—'
                    : money(vendor.quoteCents, vendor.currency ?? currency),
              })}
            </span>
            {balance !== undefined && balance > 0 && (
              <Pill tone="pending">
                {t('vendors.stillOwed', { amount: money(balance, vendor.currency ?? currency) })}
              </Pill>
            )}
          </p>
        )}

        {/* The id is load-bearing: the Save button lives in the drawer's pinned
            footer, outside this form, and `form="vendor-form"` is the only
            thing connecting them. Without it the button submits nothing and
            the drawer looks like it simply ignores you. */}
        <form id="vendor-form" onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
          {/* One attribute instead of thirty: a disabled fieldset disables every
            control inside it, so a read-only member reads the real values and
            can change none of them. `contents` keeps it out of the layout. */}
          <fieldset disabled={!canWrite} className="contents">
            <Field label={t('vendors.name')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>

            <div className="flex flex-wrap gap-3">
              <Field label={t('vendors.category')}>
                {({ id }) => (
                  <SelectInput
                    id={id}
                    value={category}
                    onChange={(e) => setCategory(e.target.value as VendorCategory)}
                  >
                    {VENDOR_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {t(`vendorCategories.${c}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
              <Field label={t('vendors.status')}>
                {({ id }) => (
                  <SelectInput
                    id={id}
                    value={status}
                    onChange={(e) => setStatus(e.target.value as VendorStatus)}
                  >
                    {VENDOR_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`vendors.statusValue.${s}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            </div>

            <div className="flex flex-wrap gap-3">
              <Field label={t('vendors.quote')} hint={currency}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    type="number"
                    min={0}
                    step="0.01"
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('vendors.deposit')} hint={t('vendors.depositHint')}>
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={describedBy}
                    type="number"
                    min={0}
                    step="0.01"
                    value={deposit}
                    onChange={(e) => setDeposit(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field label={t('vendors.contactName')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              )}
            </Field>
            <div className="flex flex-wrap gap-3">
              <Field label={t('vendors.contactEmail')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('vendors.contactPhone')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field label={t('vendors.website')}>
              {({ id }) => (
                <TextInput id={id} value={website} onChange={(e) => setWebsite(e.target.value)} />
              )}
            </Field>

            <Field label={t('vendors.notes')}>
              {({ id }) => (
                <textarea
                  id={id}
                  rows={4}
                  maxLength={5000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full rounded border border-line bg-paper px-3 py-2 text-body leading-relaxed"
                />
              )}
            </Field>

            {error && (
              <p role="alert" className="text-body text-status-declinedFg">
                {error}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              {vendor && (
                <Button
                  type="button"
                  variant="danger"
                  className="ml-auto"
                  onClick={() => setConfirming(true)}
                >
                  {t('vendors.remove')}
                </Button>
              )}
            </div>
          </fieldset>
        </form>

        {vendor && (
          <>
            <section className="flex flex-col gap-3 border-t border-line pt-5">
              <PaymentSchedule wedId={wedId} vendor={vendor} currency={currency} />
            </section>
            <section className="flex flex-col gap-3 border-t border-line pt-5">
              <EmailDraft wedId={wedId} vendorId={vendor.vendorId} />
            </section>
            <section className="flex flex-col gap-3 border-t border-line pt-5">
              <Documents wedId={wedId} vendorId={vendor.vendorId} />
            </section>
          </>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title={t('vendors.removeTitle', { name: vendor?.name ?? '' })}
        body={
          <>
            <p>{t('vendors.removeAlso')}</p>
            <p>{t('common.cannotBeUndone')}</p>
          </>
        }
        confirmLabel={t('common.remove')}
        busy={remove.isPending}
        onConfirm={() => void onDelete()}
        onClose={() => setConfirming(false)}
      />
    </Drawer>
  );
}

/**
 * A draft email to this supplier, in one of three shapes.
 *
 * **It is never sent.** Guabi has no way to email a supplier and should not
 * acquire one: the reply needs to reach the couple's own inbox, and a wedding
 * supplier receiving mail from an app nobody told them about is a supplier who
 * marks it as spam. The draft is copied out and sent from their own address,
 * which is also what makes it theirs to edit first.
 *
 * The supplier's contact name, email and phone are deliberately not sent to the
 * model (docs/security.md), so the draft opens with a bare "Hello," for the
 * couple to complete.
 */
function EmailDraft({ wedId, vendorId }: { wedId: string; vendorId: string }) {
  const { t } = useTranslation();
  const draft = useVendorEmailDraft(wedId);
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);

  const kinds: { kind: VendorEmailKind; label: string }[] = [
    { kind: 'inquiry', label: t('vendors.draftInquiry') },
    { kind: 'negotiate', label: t('vendors.draftNegotiate') },
    { kind: 'confirm', label: t('vendors.draftConfirm') },
  ];

  return (
    <>
      <h3 className="text-section">{t('vendors.draftEmail')}</h3>
      <p className="text-caption text-leaf-soft">{t('vendors.draftHint')}</p>

      <label className="flex flex-col gap-1">
        <span className="eyebrow">{t('vendors.draftNote')}</span>
        <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <div className="flex flex-wrap gap-2">
        {kinds.map(({ kind, label }) => (
          <Button
            key={kind}
            variant="secondary"
            loading={draft.isPending && draft.variables?.kind === kind}
            onClick={() =>
              draft.mutate({ vendorId, kind, ...(note.trim() ? { note: note.trim() } : {}) })
            }
          >
            {label}
          </Button>
        ))}
      </div>

      {draft.data && (
        <div className="flex flex-col gap-2">
          <textarea
            readOnly
            rows={10}
            value={draft.data.text}
            className="w-full rounded border border-line bg-cream px-3 py-2 text-body"
          />
          <span>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard
                  .writeText(draft.data.text)
                  .then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  })
                  .catch(() => undefined);
              }}
            >
              {copied ? t('vendors.draftCopied') : t('vendors.draftCopy')}
            </Button>
          </span>
        </div>
      )}

      {draft.isError && (
        <p role="alert" className="text-caption text-status-declinedFg">
          {draft.error.message}
        </p>
      )}
    </>
  );
}
